import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Calendar } from 'lucide-react';
import { heroData } from '../../data/mock';
import QuickContactModal from '../modals/QuickContactModal';
import ScheduleModal from '../modals/ScheduleModal';
import { useLanguage } from '../../context/LanguageContext';

// Stock wedding video URL (elegant, romantic)
const HERO_VIDEO_URL = "https://videos.pexels.com/video-files/3337232/3337232-uhd_2560_1440_30fps.mp4";

const HeroSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);
  const { t, currentLanguage } = useLanguage();

  const scheduleText = {
    pt: 'Agendar Reunião',
    en: 'Book Meeting',
    fr: 'Réserver Réunion',
    es: 'Agendar Reunión'
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay failed, video will show poster
      });
    }
  }, []);

  const scrollToAbout = () => {
    const element = document.querySelector('#about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="hero-section">
      {/* Background Video */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Fallback Image (shown while video loads) */}
        {!videoLoaded && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${heroData.backgroundImage})` }}
          />
        )}
        
        {/* Video */}
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
          autoPlay
          muted
          loop
          playsInline
          poster={heroData.backgroundImage}
          onLoadedData={() => setVideoLoaded(true)}
        >
          <source src={HERO_VIDEO_URL} type="video/mp4" />
        </video>
      </div>
      
      {/* Overlay - slightly darker for better text readability over video */}
      <div className="hero-overlay bg-gradient-to-b from-[#FAF9F7]/70 via-[#FAF9F7]/50 to-[#FAF9F7]/70" />
      
      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        {/* Tagline */}
        <p className="text-caption text-[#8B7D6B] mb-6 animate-fade-in-down">
          {t('hero.tagline')}
        </p>
        
        {/* Main Title */}
        <h1 className="heading-primary text-5xl md:text-7xl lg:text-8xl text-[#2C2C2C] mb-6 animate-fade-in opacity-0 animation-delay-200">
          {t('hero.title')}
        </h1>
        
        {/* Decorative Divider */}
        <div className="divider animate-fade-in opacity-0 animation-delay-400" />
        
        {/* Subtitle */}
        <p className="text-body text-lg md:text-xl text-[#6B4E37] max-w-2xl mx-auto mb-10 animate-fade-in-up opacity-0 animation-delay-400">
          {t('hero.subtitle')}
        </p>
        
        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up opacity-0 animation-delay-600">
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-primary"
            data-testid="hero-cta"
          >
            {t('hero.cta')}
          </button>
          <button
            onClick={() => setIsScheduleOpen(true)}
            className="btn-secondary flex items-center gap-2"
            data-testid="hero-schedule"
          >
            <Calendar size={18} />
            {scheduleText[currentLanguage] || scheduleText.pt}
          </button>
        </div>
      </div>
      
      {/* Scroll Indicator */}
      <button
        onClick={scrollToAbout}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 text-[#8B7D6B] animate-bounce cursor-pointer hover:text-[#6B4E37] transition-colors"
        aria-label="Scroll para baixo"
      >
        <ChevronDown size={32} />
      </button>

      {/* Quick Contact Modal */}
      <QuickContactModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />

      {/* Schedule Modal */}
      <ScheduleModal 
        isOpen={isScheduleOpen} 
        onClose={() => setIsScheduleOpen(false)} 
      />
    </section>
  );
};

export default HeroSection;
